import { createContext, Dispatch, ReactNode, useCallback, useContext, useEffect, useReducer, useRef } from "react";
import { z } from "zod";
import notificationAudio from "../../../sounds/notification.mp3";
import { useTypedSelector } from "../../../redux/hooks";
import { selectActivePage, selectCurrentUserId } from "../../../redux/slices";
import { selectQueueAlertChecker } from "../../../redux/slices/queueSlice";
import { usePrevious } from "../../../helpers";

const PanelStateSchema = z.object({
    volume: z.number(),
    isMuted: z.boolean(),
    selectedTab: z.enum(['queues', 'agents']),
    selectedPanelList: z.array(z.string()),
    selectedPanelUuid: z.string().nullish(),
    includeOffline: z.boolean(),
    agentFilterOption: z.string(),
})

type PanelState = z.infer<typeof PanelStateSchema>;

interface Props {
    playNotify: () => void;
    state: PanelState;
    reducer: Dispatch<Partial<PanelState>>;
}

interface ProviderProps {
    children: ReactNode;
}

const PanelContext = createContext<Props | null>(null);

export function usePanelContext() {
    const context = useContext(PanelContext)

    if (!context?.state) {
        throw new Error(
            'Component requiring panel context rendered without state'
        )
    }

    return {
        updatePanelState: context.reducer,
        playNotify: context.playNotify,
        ...context.state,
    }
}

function getInitialState(userUuid: string): PanelState {
    const init = localStorage.getItem(`QMP-${userUuid}`);

    if (init) {
        try {
            const optsObject: unknown = JSON.parse(init);
            const validated = PanelStateSchema.safeParse(optsObject);

            if (validated.data) {
                return validated.data;
            }
        } catch (err) {
            console.error("Failed to parse QMP opts")
        }
    }

    return {
        volume: 100,
        selectedTab: 'queues',
        selectedPanelList: [],
        selectedPanelUuid: undefined,
        includeOffline: true,
        agentFilterOption: "All Agents",
        isMuted: false,
    };

}

const PanelNotifyChecker = () => {
    const { playNotify } = usePanelContext();

    const { isQueueAlert } = useTypedSelector(selectQueueAlertChecker);

    const page = useTypedSelector(selectActivePage);
    const wasQueueAlert = usePrevious(isQueueAlert || false);

    useEffect(() => {
        const hasFocus = document.hasFocus();

        if (!!wasQueueAlert && isQueueAlert && isQueueAlert !== wasQueueAlert
            && page !== 'phone' && !hasFocus
        ) {
            playNotify();
        }

    }, [page, isQueueAlert, wasQueueAlert, playNotify])


    return <></>;
}

export const PanelProvider: React.FC<ProviderProps> = ({ children }) => {
    const currentUserUuid = useTypedSelector(selectCurrentUserId);
    const userUuidRef = useRef(currentUserUuid);

    useEffect(() => {
        userUuidRef.current = currentUserUuid;
        reducer(getInitialState(currentUserUuid));
    }, [currentUserUuid]);

    const updatePanelState = useCallback((state: PanelState, newState: Partial<PanelState>): PanelState => {
        const updatedState = {
            ...state,
            ...newState
        };

        try {
            localStorage.setItem(`QMP-${userUuidRef.current}`, JSON.stringify(updatedState));
        } catch (err) {
            console.error("Failed to save QMP options", err);
        }

        return updatedState;
    }, []);

    const [state, reducer] = useReducer(updatePanelState, currentUserUuid, getInitialState);

    useEffect(() => {
        userUuidRef.current = currentUserUuid;
        reducer(getInitialState(currentUserUuid));
    }, [currentUserUuid]);

    const audio = useRef(new Audio(notificationAudio));

    const playNotify = useCallback(() => {
        audio.current.volume = Math.max(Math.min(state.volume ?? 100, 100), 0) / 100;
        audio.current.play();
    }, [state.volume]);

    return (
        <PanelContext.Provider
            value={{
                playNotify,
                state,
                reducer,
            }}
        >
            <PanelNotifyChecker />
            {children}
        </PanelContext.Provider>
    )
}

export default PanelProvider;
